import { useCallback, useEffect } from 'react';

import { useInquiryClose } from '@/hooks/inbox/useInquiryClose';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import type { NavigationTarget } from '@/services/globalState';

type GroupedRoute = {
  conversationId: string;
  /**
   * Include status to filter out already-closed conversations.
   * Necessary because React Query cache invalidation is async,
   * so groupedRoutes may contain stale just-closed conversations.
   */
  inquiryStatus?: 'active' | 'inactive' | 'closed' | 'initializing';
};

type UseInquiryCloseNavigationProps = {
  /** Current conversation ID being viewed */
  currentConversationId: string;
  /** Current conversation status */
  currentConversationStatus: InquiryV2['inquiryStatus'];
  /** Available routes in the grouped view */
  groupedRoutes: GroupedRoute[];
  /** Current tab context - open, closed, or all */
  tabSelected: string;
  /** Whether grouped inquiries feature is enabled */
  isGroupedEnabled: boolean;
};

/**
 * Hook for capturing navigation target during inquiry close flow.
 *
 * Responsibilities:
 * 1. Compute next conversation to navigate to based on grouped routes
 * 2. Capture navigation target when close is initiated, status is 'closing'
 * 3. Store target in state via SET_NAVIGATION_TARGET action
 *
 * Navigation execution is handled by InquiryCloseNavigationEffect at the
 * app level, which responds to status === 'complete'.
 *
 * By capturing navigation target at close time, we avoid re-evaluating
 * feature flags and tab state after feedback.
 */
export function useInquiryCloseNavigation({
  currentConversationId,
  groupedRoutes,
  tabSelected,
  isGroupedEnabled,
}: UseInquiryCloseNavigationProps) {
  const { state, isFeedbackActive, setNavigationTarget } = useInquiryClose();

  /**
   * Find the next available open conversation in the group.
   * Returns null if no other open conversations exist.
   */
  const getNextConversationId = useCallback((): string | null => {
    const nextRoute = groupedRoutes.find(
      (route) => route.conversationId !== currentConversationId && route.inquiryStatus !== 'closed'
    );

    return nextRoute?.conversationId ?? null;
  }, [groupedRoutes, currentConversationId]);

  /**
   * Compute navigation target based on current state.
   * Called when close is initiated to capture target before navigation.
   */
  const computeNavigationTarget = useCallback((): NavigationTarget => {
    const shouldNavigate = isGroupedEnabled && tabSelected === 'open';

    const nextConversationId = shouldNavigate ? getNextConversationId() : null;

    return {
      shouldNavigate,
      nextConversationId,
    };
  }, [isGroupedEnabled, tabSelected, getNextConversationId]);

  /**
   * Capture and store navigation target via SET_NAVIGATION_TARGET action.
   */
  const captureNavigationTarget = useCallback(() => {
    const target = computeNavigationTarget();

    setNavigationTarget(target);

    return target;
  }, [computeNavigationTarget, setNavigationTarget]);

  /**
   * Auto-capture navigation target when status becomes 'closing'.
   * This must happen before feedback starts so the target is available
   * when InquiryCloseNavigationEffect executes navigation.
   */
  useEffect(() => {
    if (state.status !== 'closing') {
      return;
    }

    if (state.conversationId !== currentConversationId) {
      return;
    }

    if (state.navigationTarget !== null) {
      return;
    }

    captureNavigationTarget();
  }, [
    state.status,
    state.conversationId,
    state.navigationTarget,
    state.startedAt,
    currentConversationId,
    captureNavigationTarget,
  ]);

  return {
    isFeedbackActive,
    state,
    captureNavigationTarget,
    getNextConversationId,
  };
}
